import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  User,
  Stethoscope,
  MapPin,
  Phone,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  PartyPopper,
  MessageCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cancel reason options
const CANCEL_REASONS = [
  { value: 'cant_make_it', label: "Can't make it" },
  { value: 'found_another', label: 'Found another clinic' },
  { value: 'feeling_better', label: 'Feeling better' },
  { value: 'financial', label: 'Financial reasons' },
  { value: 'other', label: 'Other' }
];

const PatientActionPage = () => {
  const { appointmentId, token } = useParams();
  
  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [alreadyActioned, setAlreadyActioned] = useState(false);
  const [actionedStatus, setActionedStatus] = useState('');
  
  // Action states
  const [currentView, setCurrentView] = useState('main'); // main, confirm, cancel, reschedule, callback, success
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Cancel form states
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  
  // Reschedule states
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(0); // 0 = current month view
  
  // Callback states
  const [callbackNotes, setCallbackNotes] = useState('');

  // Fetch appointment details
  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/patient/appointment/${appointmentId}/${token}`);
        
        if (res.data.already_actioned) {
          setAlreadyActioned(true);
          setActionedStatus(res.data.status);
        } else {
          setAppointmentData(res.data);
        }
      } catch (err) {
        console.error('Error fetching appointment:', err);
        setError(err.response?.data?.detail || 'Invalid or expired link. Please contact the clinic.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAppointment();
  }, [appointmentId, token]);

  // Fetch available slots for rescheduling
  const fetchAvailableSlots = async () => {
    setSlotsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/patient/available-slots/${appointmentId}/${token}`);
      setAvailableSlots(res.data.available_slots || []);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setError('Failed to load available slots');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Handle confirm action
  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/patient/confirm/${appointmentId}/${token}`);
      if (res.data.success) {
        setSuccessMessage(res.data.message);
        setCurrentView('success');
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to confirm appointment');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel action
  const handleCancel = async () => {
    if (!cancelReason) {
      setError('Please select a reason for cancellation');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/patient/cancel/${appointmentId}/${token}`, {
        reason_category: cancelReason,
        notes: cancelNotes
      });
      if (res.data.success) {
        setSuccessMessage(res.data.message);
        setCurrentView('success');
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reschedule action
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select a date and time');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/patient/reschedule/${appointmentId}/${token}`, {
        new_date: selectedDate,
        new_time: selectedTime
      });
      if (res.data.success) {
        setSuccessMessage(res.data.message);
        setCurrentView('success');
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reschedule appointment');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle callback request
  const handleCallbackRequest = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/patient/callback-request/${appointmentId}/${token}`, {
        preferred_dates: [],
        notes: callbackNotes
      });
      if (res.data.success) {
        setSuccessMessage(res.data.message);
        setCurrentView('success');
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit callback request');
    } finally {
      setActionLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your appointment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && currentView === 'main') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already actioned state
  if (alreadyActioned) {
    const statusConfig = {
      confirmed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', message: 'Your appointment has been confirmed.' },
      cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', message: 'This appointment has been cancelled.' },
      rescheduled: { icon: Calendar, color: 'text-blue-500', bg: 'bg-blue-50', message: 'This appointment has been rescheduled.' }
    };
    const config = statusConfig[actionedStatus] || statusConfig.confirmed;
    const Icon = config.icon;

    return (
      <div className={`min-h-screen ${config.bg} flex items-center justify-center p-4`}>
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Icon className={`w-16 h-16 ${config.color} mx-auto mb-4`} />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already {actionedStatus}</h2>
            <p className="text-gray-600">{config.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (currentView === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <PartyPopper className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Done!</h2>
            <p className="text-gray-600">{successMessage}</p>
            <div className="mt-6 p-4 bg-green-100 rounded-lg">
              <p className="text-sm text-green-800">
                You can close this window now.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const appointment = appointmentData?.appointment;
  const clinicName = appointmentData?.clinic_name || 'Our Clinic';

  // Main action selection view
  if (currentView === 'main') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{clinicName}</h1>
            <p className="text-gray-600">Manage Your Appointment</p>
          </div>

          {/* Appointment Card */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 text-gray-700">
                <User className="w-5 h-5 text-gray-400" />
                <span className="font-medium">{appointment?.patient_name}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Stethoscope className="w-5 h-5 text-gray-400" />
                <span>{appointment?.service_name}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span>{formatDate(appointment?.scheduled_date)}</span>
              </div>
              {appointment?.scheduled_time && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>{appointment?.scheduled_time}</span>
                </div>
              )}
              {appointment?.ref_number && (
                <Badge variant="outline" className="mt-2">
                  Ref: {appointment?.ref_number}
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleConfirm}
              disabled={actionLoading}
              className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
              data-testid="confirm-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              Confirm Appointment
            </Button>

            <Button
              onClick={() => setCurrentView('cancel')}
              variant="outline"
              className="w-full h-14 text-lg border-red-200 text-red-600 hover:bg-red-50"
              data-testid="cancel-btn"
            >
              <XCircle className="w-5 h-5 mr-2" />
              Cancel Appointment
            </Button>

            <Button
              onClick={() => {
                setCurrentView('reschedule');
                fetchAvailableSlots();
              }}
              variant="outline"
              className="w-full h-14 text-lg border-blue-200 text-blue-600 hover:bg-blue-50"
              data-testid="reschedule-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Reschedule Appointment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Cancel view
  if (currentView === 'cancel') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            onClick={() => {
              setCurrentView('main');
              setError(null);
            }}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-6 h-6" />
                Cancel Appointment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                We&apos;re sorry to see you go. Please let us know why you&apos;re cancelling:
              </p>

              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Reason Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason for cancellation *</label>
                <div className="space-y-2">
                  {CANCEL_REASONS.map((reason) => (
                    <button
                      key={reason.value}
                      onClick={() => {
                        setCancelReason(reason.value);
                        setError(null);
                      }}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        cancelReason === reason.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {reason.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700">Additional notes (optional)</label>
                <textarea
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  placeholder="Any additional information..."
                  className="w-full mt-1 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={3}
                />
              </div>

              <Button
                onClick={handleCancel}
                disabled={actionLoading || !cancelReason}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="submit-cancel-btn"
              >
                {actionLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : null}
                Confirm Cancellation
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reschedule view
  if (currentView === 'reschedule') {
    // Group slots by week for easier navigation
    const groupedSlots = availableSlots.reduce((acc, slot) => {
      const date = new Date(slot.date);
      const weekNum = Math.floor((date.getDate() - 1) / 7);
      if (!acc[weekNum]) acc[weekNum] = [];
      acc[weekNum].push(slot);
      return acc;
    }, {});

    const weeks = Object.values(groupedSlots);
    const currentWeekSlots = weeks[currentMonth] || [];

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            onClick={() => {
              setCurrentView('main');
              setSelectedDate(null);
              setSelectedTime(null);
              setError(null);
            }}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <Calendar className="w-6 h-6" />
                Reschedule Appointment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {slotsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                  <p className="text-gray-600">Loading available slots...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No available slots found for the next 30 days.</p>
                  <Button
                    onClick={() => setCurrentView('callback')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Request Callback
                  </Button>
                </div>
              ) : (
                <>
                  {/* Week Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentMonth(Math.max(0, currentMonth - 1))}
                      disabled={currentMonth === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium text-gray-600">
                      Week {currentMonth + 1} of {weeks.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentMonth(Math.min(weeks.length - 1, currentMonth + 1))}
                      disabled={currentMonth >= weeks.length - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Date Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Select Date</label>
                    <div className="grid grid-cols-2 gap-2">
                      {currentWeekSlots.map((slot) => (
                        <button
                          key={slot.date}
                          onClick={() => {
                            setSelectedDate(slot.date);
                            setSelectedTime(null);
                            setError(null);
                          }}
                          className={`p-3 text-left rounded-lg border transition-colors ${
                            selectedDate === slot.date
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">{slot.formatted_date}</div>
                          <div className="text-xs text-gray-500">{slot.day_name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Selection */}
                  {selectedDate && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Select Time</label>
                      <div className="grid grid-cols-4 gap-2">
                        {availableSlots
                          .find((s) => s.date === selectedDate)
                          ?.slots.map((time) => (
                            <button
                              key={time}
                              onClick={() => {
                                setSelectedTime(time);
                                setError(null);
                              }}
                              className={`p-2 text-center rounded-lg border transition-colors text-sm ${
                                selectedTime === time
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {time === 'queue' ? 'Queue' : time}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Confirm Reschedule Button */}
                  <Button
                    onClick={handleReschedule}
                    disabled={actionLoading || !selectedDate || !selectedTime}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-reschedule-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : null}
                    Confirm Reschedule
                  </Button>

                  {/* Callback Option */}
                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-2">Can&apos;t find a suitable slot?</p>
                    <Button
                      variant="link"
                      onClick={() => setCurrentView('callback')}
                      className="text-blue-600"
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      Request a callback
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Callback request view
  if (currentView === 'callback') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            onClick={() => {
              setCurrentView('reschedule');
              setError(null);
            }}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <Phone className="w-6 h-6" />
                Request Callback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Our team will call you to help find a suitable appointment time.
              </p>

              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Any preferences or notes? (optional)
                </label>
                <textarea
                  value={callbackNotes}
                  onChange={(e) => setCallbackNotes(e.target.value)}
                  placeholder="E.g., Preferred days, morning/afternoon preference, etc."
                  className="w-full mt-1 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={4}
                />
              </div>

              <Button
                onClick={handleCallbackRequest}
                disabled={actionLoading}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="submit-callback-btn"
              >
                {actionLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Phone className="w-5 h-5 mr-2" />
                )}
                Request Callback
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default PatientActionPage;
